// Maps paths between MAJOR UI snapshots and keeps ?v= pinned across navigations.

export const VERSION_QUERY_KEY = 'v';

/**
 * Path valid for the selected version id (v1/v3 classic IA vs v2 journeys IA).
 * v3 is cut from v1 and keeps its paths, so `?v=v3` remaps journeys paths to
 * classic exactly as `?v=v1` does.
 *
 * v4's journeys each own a project route (`/projects/<id>/<route>`, from its
 * journeys registry, which registers them below), and its retired
 * `/projects/<id>/journeys/…` links redirect there. v1–v3 route neither, so
 * they send both to the project home. v4 needs no map of its own: its router
 * opens the first journey for any project path no journey owns.
 */
export function canonicalPathForVersion(pathname: string, versionId: string): string {
  if (versionId === 'v1' || versionId === 'v3') {
    return mapJourneysPathToClassic(mapV4JourneyPathToProjectHome(pathname));
  }
  if (versionId === 'v2') return mapClassicPathToJourneys(mapV4JourneyPathToProjectHome(pathname));
  return pathname;
}

/** Project route segments only v4 routes (its journeys), registered by v4's journeys registry when v4 loads. */
const V4_ONLY_PROJECT_ROUTES = new Set<string>(['journeys']);

export function registerVersionOnlyProjectRoutes(routes: readonly string[]): void {
  for (const route of routes) V4_ONLY_PROJECT_ROUTES.add(route);
}

function mapV4JourneyPathToProjectHome(pathname: string): string {
  const m = pathname.match(/^\/projects\/([^/]+)\/([^/]+)(?:\/.*)?$/);
  return m && V4_ONLY_PROJECT_ROUTES.has(m[2]) ? `/projects/${m[1]}` : pathname;
}

function mapJourneysPathToClassic(pathname: string): string {
  let m = pathname.match(/^\/projects\/([^/]+)\/cases\/([^/]+)\/([^/]+)$/);
  if (m) {
    const [, projectId, , tab] = m;
    const tabDest: Record<string, string> = {
      assumptions: '/data',
      run: '/plan',
      results: '/horizon',
      why: '/trace',
      viewer: '/viewer',
    };
    const suffix = tabDest[tab];
    if (suffix) return `/projects/${projectId}${suffix}`;
  }

  m = pathname.match(/^\/projects\/([^/]+)\/cases\/([^/]+)$/);
  if (m) return `/projects/${m[1]}/workbench`;

  m = pathname.match(/^\/projects\/([^/]+)\/cases$/);
  if (m) return `/projects/${m[1]}/workbench`;

  m = pathname.match(/^\/projects\/([^/]+)\/case$/);
  if (m) return `/projects/${m[1]}/workbench`;

  m = pathname.match(/^\/projects\/([^/]+)\/model\/([^/]+)$/);
  if (m) return `/projects/${m[1]}/system/${m[2]}`;

  m = pathname.match(/^\/projects\/([^/]+)\/model$/);
  if (m) return `/projects/${m[1]}/system`;

  return pathname;
}

function mapClassicPathToJourneys(pathname: string): string {
  let m = pathname.match(/^\/projects\/([^/]+)\/workbench$/);
  if (m) return `/projects/${m[1]}/cases`;

  m = pathname.match(/^\/projects\/([^/]+)\/system\/([^/]+)$/);
  if (m) return `/projects/${m[1]}/model/${m[2]}`;

  m = pathname.match(/^\/projects\/([^/]+)\/system$/);
  if (m) return `/projects/${m[1]}/model`;

  // `/viewer` is resolved by v2's ViewerLegacyRedirect (?case= → Case › Viewer tab).
  for (const seg of ['plan', 'horizon', 'trace'] as const) {
    m = pathname.match(new RegExp(`^/projects/([^/]+)/${seg}$`));
    if (m) return `/projects/${m[1]}/case`;
  }

  return pathname;
}
